import type { ReactNode } from 'react'
import { Sparkles } from 'lucide-react'
import { useDocumentAutofillContext } from './DocumentAutofillContext'

interface AutofillFieldProps {
    /** The form key the document fills (the driver draft key, the truck path, the contractor path). */
    field: string
    /** Classes for the wrapper, e.g. `min-w-0` when it is a grid cell. */
    className?: string
    children: ReactNode
}

/**
 * The input box of every ds2 field carries `data-field-box`. Its focus state is a ring, so this marks it with an OUTLINE, inset
 * so no container clips it: focus and the document's mark show together.
 */
const BOX_MARK = {
    filled: '[&_[data-field-box]]:outline-2 [&_[data-field-box]]:outline-solid [&_[data-field-box]]:-outline-offset-2 [&_[data-field-box]]:outline-success/70',
    differs: '[&_[data-field-box]]:outline-2 [&_[data-field-box]]:outline-solid [&_[data-field-box]]:-outline-offset-2 [&_[data-field-box]]:outline-warning/80',
} as const

const USE_BUTTON =
    'shrink-0 rounded-md bg-accent-soft px-1.5 text-[13px] font-semibold leading-5 text-accent hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent'

/**
 * A form field a document can fill. Green outline and a ✦ on the label row while it holds the value the document filled (HR still
 * checks it); orange outline and the document's value, one click away, while HR's own value differs or HR emptied what a document
 * filled. When the card's files disagree, every value is listed under the field with the file it came from, and nothing is filled
 * until HR uses one. Typing clears the marks.
 * `data-autofill-field` lets a finished read bring HR to the fields that need a look.
 *
 * The marks sit at the wrapper's top right, on the field's label line: wrap the field together with its label. A field whose label
 * is drawn outside it (a `FieldRow`) is wrapped with that row, or the document's value would cover the input.
 *
 * Inside a form that reads documents the field always sits in the same wrapper, highlighted or not: switching between a bare field
 * and a wrapped one would make React rebuild the input, and HR would lose focus after the first character typed over a value.
 * Outside such a form, it renders the field as is.
 */
export const AutofillField = ({ field, className, children }: AutofillFieldProps) => {
    const autofill = useDocumentAutofillContext()
    if (!autofill) return <>{children}</>
    const state = autofill.fieldOf(field)
    const [single] = state?.state === 'differs' && !state.disagree ? state.offers : []

    return (
        <div className={`relative ${className ?? ''} ${state ? BOX_MARK[state.state] : ''}`} data-autofill={state?.state} data-autofill-field={field}>
            {children}
            {state?.state === 'filled' && (
                <span
                    className="absolute right-0 top-0 flex h-5 items-center text-success"
                    role="img"
                    title="Filled from the document — check it"
                    aria-label="Filled from the document — check it"
                >
                    <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                </span>
            )}
            {single && (
                <span className="absolute right-0 top-0 flex h-5 max-w-[70%] items-center gap-1.5 text-[13px] text-warning-soft-foreground">
                    <Sparkles className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    <span className="truncate" title={String(single.value)}>
                        Document: <span className="font-semibold text-foreground">{String(single.value)}</span>
                    </span>
                    <button
                        type="button"
                        className={USE_BUTTON}
                        aria-label={`Use the document's value, ${String(single.value)}`}
                        onClick={() => autofill.accept(field, single.value)}
                    >
                        Use
                    </button>
                </span>
            )}
            {state?.state === 'differs' && state.disagree && (
                <div className="mt-1 space-y-1 text-[13px] text-warning-soft-foreground" role="group" aria-label="The documents disagree">
                    <p className="flex items-center gap-1.5">
                        <Sparkles className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        The documents disagree. Pick the right value:
                    </p>
                    {state.offers.map((offer) => (
                        <p key={String(offer.value)} className="flex min-w-0 items-center gap-1.5 pl-5">
                            <span className="min-w-0 truncate" title={`${String(offer.value)} — ${offer.fileNames.join(', ')}`}>
                                <span className="font-semibold text-foreground">{String(offer.value)}</span>
                                <span className="text-foreground-muted"> · {offer.fileNames.join(', ')}</span>
                            </span>
                            <button
                                type="button"
                                className={USE_BUTTON}
                                aria-label={`Use ${String(offer.value)}, from ${offer.fileNames.join(', ')}`}
                                onClick={() => autofill.accept(field, offer.value)}
                            >
                                Use
                            </button>
                        </p>
                    ))}
                </div>
            )}
        </div>
    )
}
